import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, TrendingUp, BarChart3, Users } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { getEstimatedListingPrice, getGmpPercentage } from "@/lib/gmpUtils";
import GMPChart from "@/components/GMPChart";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatInr, formatIssueSizeCr, formatSignedInr } from "@/lib/numberFormat";

const IPODetail = () => {
  const { id } = useParams();
  const { ipos } = useData();
  const ipo = ipos.find((i) => i.id === id);

  if (!ipo) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">IPO not found.</p>
        <Link to="/ipos" className="text-secondary hover:underline mt-4 inline-block">← Back to IPOs</Link>
      </div>
    );
  }

  const pct = getGmpPercentage(ipo.gmp, ipo.priceBand);
  const yPct = getGmpPercentage(ipo.yesterdayGmp, ipo.priceBand);
  const estimatedListingPrice = getEstimatedListingPrice(ipo.priceBand, ipo.gmp, ipo.estimatedListing);

  return (
    <div className="py-10 md:py-14">
      <div className="container mx-auto px-4 max-w-4xl">
        <Link to="/ipos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={16} /> Back to IPOs
        </Link>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">{ipo.company}</h1>
            <p className="text-muted-foreground mt-1">{ipo.exchange}</p>
          </div>
          <span className={`text-base ${
            ipo.status === "upcoming" ? "badge-info" :
            ipo.status === "ongoing" ? "badge-success" :
            ipo.status === "closing_today" ? "badge-warning" :
            ipo.status === "allotment" ? "badge-secondary" :
            ipo.status === "allotted" ? "badge-success" :
            ipo.status === "listing_today" ? "badge-primary" :
            ipo.status === "listed" ? "badge-success" :
            ipo.status === "closed" ? "badge-destructive" :
            "badge-warning"
          }`}>
            {ipo.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Price Band", value: `₹${ipo.priceBand}`, icon: TrendingUp },
            { label: "Issue Size", value: formatIssueSizeCr(ipo.issueSize), icon: BarChart3 },
            { label: "Lot Size", value: `${ipo.lotSize} shares`, icon: Users },
            { label: "Subscription", value: ipo.subscriptionStatus, icon: TrendingUp },
          ].map((m) => (
            <div key={m.label} className="finance-card p-4">
              <m.icon size={18} className="text-secondary mb-2" />
              <span className="text-xs text-muted-foreground">{m.label}</span>
              <p className="font-display font-semibold text-foreground mt-0.5">{m.value}</p>
            </div>
          ))}
        </div>

        <div className="finance-card p-5 mb-6">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-secondary" /> Important Dates
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-muted-foreground">Open Date</span><p className="font-medium text-foreground">{formatDisplayDate(ipo.openDate)}</p></div>
            <div><span className="text-muted-foreground">Close Date</span><p className="font-medium text-foreground">{formatDisplayDate(ipo.closeDate)}</p></div>
            <div><span className="text-muted-foreground">Allotment Date</span><p className="font-medium text-foreground">{ipo.allotmentDate ? formatDisplayDate(ipo.allotmentDate) : "TBA"}</p></div>
            <div><span className="text-muted-foreground">Listing Date</span><p className="font-medium text-foreground">{ipo.listingDate ? formatDisplayDate(ipo.listingDate) : "TBA"}</p></div>
          </div>
        </div>

        <div className="finance-card p-5 mb-6">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-secondary" /> GMP Details
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Today's GMP</span>
              <p className={`font-semibold text-lg ${ipo.gmp >= 0 ? "text-success" : "text-destructive"}`}>{formatSignedInr(ipo.gmp)} <span className="text-sm">({pct}%)</span></p>
            </div>
            <div>
              <span className="text-muted-foreground">Yesterday's GMP</span>
              <p className={`font-semibold text-lg ${ipo.yesterdayGmp >= 0 ? "text-foreground" : "text-destructive"}`}>{formatSignedInr(ipo.yesterdayGmp)} <span className="text-sm text-muted-foreground">({yPct}%)</span></p>
            </div>
            <div>
              <span className="text-muted-foreground">Est. Listing Price</span>
              <p className="font-semibold text-foreground text-lg">{formatInr(Number(estimatedListingPrice))}</p>
            </div>
          </div>
        </div>

        {/* GMP Graph */}
        <div className="finance-card p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold flex items-center gap-2">
              <TrendingUp size={18} className="text-secondary" /> GMP Trend Graph
            </h3>
          </div>
          {ipo.id && <GMPChart ipoId={ipo.id} />}
        </div>

        <div className="p-4 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>Disclaimer:</strong> IPO Grey Market Premium (GMP) information shared on this website is based on market sources, industry discussions, and publicly available information. GMP is unofficial, not regulated by SEBI, and may change without notice. We do not guarantee accuracy or future returns.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IPODetail;

